import { useCallback, useEffect, useMemo, useState } from 'react';
import { Key, Compass, AlertCircle, RefreshCw } from 'lucide-react';
import { AppShell, type Route } from './components/AppShell';
import { CapsulesList } from './components/CapsulesList';
import { CapsuleDetail } from './components/CapsuleDetail';
import { SendCapsule } from './components/SendCapsule';
import { SettingsPanel } from './components/SettingsPanel';
import { LoginPanel } from './components/LoginPanel';
import { EmptyState } from './components/EmptyState';
import { DragOverlay } from './components/DragOverlay';
import { UpdateBanner } from './components/UpdateBanner';
import { TerminalPanel } from './components/TerminalPanel';
import { Landing } from './components/landing/Landing';
import { clearLandingTheme } from './components/landing/theme';
import { AuthCallback, type CallbackOutcome } from './components/AuthCallback';
import { HandoffNotice } from './components/HandoffNotice';
import { CollectClient } from './lib/api';
import { hasCredentials, loadSettings } from './lib/settings';
import { afterLogin, buildPath, documentTitle, parseLocation, resolveRedirect, type AppLocation } from './lib/route';
import { accessKeyLoginEnabled, trustConfig } from './lib/oidc';
import { getSession, onSessionChange, restoreSession, signOut, type PassSession } from './lib/session';
import { useRuneHandoff } from './lib/useRuneHandoff';
import { isTauri, onTauriFilesDropped, onTauriDragState, tauriDropToFiles } from './lib/tauri';
import type { Capsule, Settings } from './types';

function currentLocation(): AppLocation {
  return parseLocation(window.location.pathname, window.location.search);
}

/** Nav item highlighted for a view (null = outside the nav). */
function navRoute(loc: AppLocation): Route | null {
  switch (loc.view) {
    case 'capsules':
    case 'capsule': return 'capsules';
    case 'send': return 'send';
    case 'settings': return 'settings';
    case 'terminal': return 'terminal';
    default: return null;
  }
}

// "Sign in with Ordinal Pass" (VITE_TRUST_*) and the access-key fallback — build-time flags.
const TRUST = trustConfig();
const KEY_LOGIN = accessKeyLoginEnabled();

export function App() {
  const [settings, setSettings] = useState<Settings>(() => loadSettings());
  const [loc, setLoc] = useState<AppLocation>(() => currentLocation());
  const [selected, setSelected] = useState<Capsule | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const [tauriDragHover, setTauriDragHover] = useState(false);
  const [pass, setPass] = useState<PassSession | null>(() => getSession());
  // On load, a refresh cookie may hold an Ordinal Pass session (not on the callback page itself).
  const [restoring, setRestoring] = useState(() => Boolean(TRUST) && currentLocation().view !== 'auth-callback');
  const [loginNotice, setLoginNotice] = useState<string | null>(null);

  const client = useMemo(() => new CollectClient(settings, pass?.accessToken), [settings, pass]);
  const signedIn = Boolean(pass) || hasCredentials(settings);

  useEffect(() => onSessionChange(setPass), []);

  useEffect(() => {
    if (!restoring) return;
    restoreSession().catch(() => null).finally(() => setRestoring(false));
  }, [restoring]);

  // Renew the in-memory access token a minute before it expires.
  useEffect(() => {
    if (!pass) return;
    const ms = Math.max(5_000, pass.expiresAt - Date.now() - 60_000);
    const t = window.setTimeout(() => { restoreSession().catch(() => null); }, ms);
    return () => window.clearTimeout(t);
  }, [pass]);

  // Path-based routing: history.pushState + popstate. vercel.json rewrites
  // deep links (/login, /send, /capsules/42 …) to index.html.
  const navigate = useCallback((next: AppLocation, opts: { replace?: boolean } = {}) => {
    const path = buildPath(next);
    if (path !== window.location.pathname + window.location.search) {
      window.history[opts.replace ? 'replaceState' : 'pushState'](null, '', path);
    }
    setLoc(next);
  }, []);

  useEffect(() => {
    const onPop = () => setLoc(currentLocation());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // `/` and `/login` resolve to a real screen; render the target right away
  // and fix the address bar after.
  const redirect = resolveRedirect(loc, signedIn);
  const redirectPath = redirect ? buildPath(redirect) : null;
  const view = redirect ?? loc;

  // Rune handoff (/send?…&handoff=1, lib/runeHandoff.ts): listens while the link carries it —
  // on /send and on /login — and keeps what arrived in memory across the in-page sign-in.
  const handoffOn = Boolean(view.handoff && view.template) && (view.view === 'send' || view.view === 'login');
  const handoff = useRuneHandoff(handoffOn, view.template);
  useEffect(() => {
    if (redirect) navigate(redirect, { replace: true });
    // redirectPath identifies `redirect`; the object itself is new on every render
  }, [redirectPath, navigate]);

  // The landing theme (<html data-landing-theme>, set before paint by index.html on `/`)
  // belongs to the landing and its "Loading Collect…" step only; every other screen is the
  // dark app shell. (Landing also clears it when it unmounts.)
  const landingShown = view.view === 'root' && !signedIn;
  useEffect(() => {
    if (!restoring && !landingShown) clearLandingTheme();
  }, [restoring, landingShown]);

  // Tab and history titles per screen; on the landing it is index.html's own <title>.
  const title = documentTitle(view, landingShown);
  useEffect(() => {
    if (!restoring) document.title = title;
  }, [restoring, title]);

  // Tauri native drag-drop: register listeners for the Rust-emitted events.
  // Both helpers no-op outside the Tauri shell, so this is safe in browser builds.
  useEffect(() => {
    let stopFiles: () => void = () => {};
    let stopDrag: () => void = () => {};
    let cancelled = false;
    (async () => {
      stopDrag = await onTauriDragState((s) => {
        if (cancelled) return;
        setTauriDragHover(s.state === 'enter');
      });
      stopFiles = await onTauriFilesDropped((payload) => {
        if (cancelled) return;
        setTauriDragHover(false);
        const files = tauriDropToFiles(payload);
        setPendingFiles(files);
        // Route the drop into Send (or the sign-in screen if no creds yet)
        const ws = loadSettings();
        setSelected(null);
        navigate(hasCredentials(ws) ? { view: 'send' } : { view: 'login' });
      });
    })();
    return () => {
      cancelled = true;
      stopFiles();
      stopDrag();
    };
  }, [navigate]);

  const onSettingsSaved = (s: Settings) => {
    setSettings(s);
    setRefreshKey((k) => k + 1);
    if (hasCredentials(s)) {
      navigate({ view: 'capsules' });
    }
  };

  const onSignedIn = (s: Settings) => {
    setSettings(s);
    setRefreshKey((k) => k + 1);
    navigate(afterLogin(view), { replace: true });
  };

  const goLogin = () => {
    window.scrollTo(0, 0);
    navigate({
      view: 'login',
      ...(view.template ? { template: view.template } : {}),
      ...(view.from ? { from: view.from } : {}),
      ...(view.template && view.handoff ? { handoff: true as const } : {}),
    });
  };

  const onCallbackDone = useCallback((o: CallbackOutcome) => {
    const back: AppLocation = {
      view: 'login',
      ...(o.returnTo?.from ? { from: o.returnTo.from } : {}),
      ...(o.returnTo?.template ? { template: o.returnTo.template } : {}),
      // back to the rune handoff: /send?…&handoff=1 asks the rune (still open) again
      ...(o.returnTo?.template && o.returnTo.handoff === true ? { handoff: true as const } : {}),
    };
    if (o.ok) {
      setLoginNotice(null);
      setRefreshKey((k) => k + 1);
      navigate(afterLogin(back), { replace: true });
    } else {
      setLoginNotice(o.message);
      navigate(back, { replace: true });
    }
  }, [navigate]);

  const onSignOut = async () => {
    await signOut();
    setSelected(null);
    navigate({ view: 'root' });
  };

  if (view.view === 'auth-callback' && TRUST) {
    return <AuthCallback onDone={onCallbackDone} />;
  }

  if (restoring) {
    return (
      <div className="collect-loading min-h-screen flex items-center justify-center gap-3">
        <RefreshCw className="animate-spin" size={18} />
        <span>Loading Collect…</span>
      </div>
    );
  }

  // `/` signed out: the Awakening landing (D-1 A), outside the app shell.
  if (view.view === 'root' && !signedIn) {
    return <Landing passSignIn={Boolean(TRUST)} onSignIn={goLogin} />;
  }

  return (
    <AppShell
      client={client}
      route={navRoute(view)}
      onRouteChange={(r) => { setSelected(null); navigate({ view: r }); }}
      refreshKey={refreshKey}
      pass={pass}
      onSignOut={pass ? onSignOut : undefined}
    >
      {view.view === 'login' && view.handoff && view.template && (
        <div className="max-w-xl mx-auto">
          <HandoffNotice state={handoff} template={view.template} signedIn={false} passSignIn={Boolean(TRUST)} />
        </div>
      )}

      {view.view === 'login' && (
        <LoginPanel
          initial={settings}
          from={view.from}
          template={view.template}
          handoff={Boolean(view.handoff)}
          trust={TRUST}
          keyLogin={KEY_LOGIN}
          notice={loginNotice}
          onSignedIn={onSignedIn}
        />
      )}

      {view.view === 'settings' && (
        <SettingsPanel initial={settings} onSaved={onSettingsSaved} />
      )}

      {view.view === 'send' && !signedIn && view.handoff && view.template && (
        <div className="max-w-xl mx-auto">
          <HandoffNotice state={handoff} template={view.template} signedIn={false} passSignIn={Boolean(TRUST)} />
        </div>
      )}

      {(view.view === 'capsules' || view.view === 'capsule' || view.view === 'send') && !signedIn && (
        <EmptyState
          icon={<Key size={48} />}
          title={view.view === 'send' ? 'Sign in to send' : 'Connect to your HIVE'}
          description={TRUST ? 'Sign in with your Ordinal Pass to load and send capsules.' : 'Sign in with your Collect access key to load and send capsules.'}
          action={<button className="btn btn-primary" onClick={goLogin}>Sign in</button>}
        />
      )}

      {view.view === 'capsules' && signedIn && (
        <CapsulesList
          client={client}
          onSelect={(c) => { setSelected(c); navigate({ view: 'capsule', capsuleId: c.id }); }}
          refreshKey={refreshKey}
          onGoToSettings={() => navigate({ view: 'settings' })}
        />
      )}

      {view.view === 'capsule' && signedIn && view.capsuleId && (
        <CapsuleRoute
          key={view.capsuleId}
          client={client}
          capsuleId={view.capsuleId}
          cached={selected?.id === view.capsuleId ? selected : null}
          onBack={() => { setSelected(null); navigate({ view: 'capsules' }); }}
        />
      )}

      {view.view === 'terminal' && isTauri() && (
        <TerminalPanel />
      )}

      {view.view === 'send' && signedIn && (
        <SendCapsule
          key={view.template || '-'}
          client={client}
          initialTemplate={view.template}
          handoff={view.handoff ? handoff : undefined}
          initialFiles={pendingFiles ?? undefined}
          onFilesConsumed={() => setPendingFiles(null)}
          onSent={() => {
            // Refresh capsules list when user navigates back
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {(view.view === 'not-found' || view.view === 'auth-callback' || (view.view === 'terminal' && !isTauri())) && (
        <EmptyState
          icon={<Compass size={48} />}
          title="Nothing here"
          description={`${window.location.pathname} is not a Collect page.`}
          action={<button className="btn btn-primary" onClick={() => navigate({ view: 'root' }, { replace: true })}>Open Collect</button>}
        />
      )}

      {/* Top-level Tauri drag overlay — visible on every route while a native drag is hovering. */}
      <DragOverlay show={tauriDragHover} label="Drop to start a capsule" hint="Files become sources in a new HIVE capsule" />

      {/* Top-level update banner — only renders inside the Tauri shell when a newer version is published. */}
      <UpdateBanner />
    </AppShell>
  );
}

/**
 * /capsules/:id — uses the capsule picked in the list when there is one,
 * otherwise loads it (deep link, reload, back/forward).
 */
function CapsuleRoute({
  client,
  capsuleId,
  cached,
  onBack,
}: {
  client: CollectClient;
  capsuleId: number;
  cached: Capsule | null;
  onBack: () => void;
}) {
  const [capsule, setCapsule] = useState<Capsule | null>(cached);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    client.getCapsule(capsuleId)
      .then((c) => { if (!cancelled) setCapsule(c); })
      .catch((e: any) => {
        if (cancelled) return;
        setError(['CORS_BLOCKED', 'NETWORK', 'UNAVAILABLE'].includes(e?.code) ? e.message : `${e?.code || 'ERROR'}: ${e?.message || e}`);
      });
    return () => { cancelled = true; };
  }, [client, capsuleId, cached]);

  if (capsule) return <CapsuleDetail client={client} capsule={capsule} onBack={onBack} />;
  if (error) {
    return (
      <EmptyState
        icon={<AlertCircle size={48} />}
        title={`Cannot open capsule #${capsuleId}`}
        description={error}
        action={<button className="btn btn-primary" onClick={onBack}>Back to capsules</button>}
      />
    );
  }
  return (
    <div className="flex items-center gap-3 justify-center py-20" style={{ color: 'rgba(240,253,244,0.5)' }}>
      <RefreshCw className="animate-spin" size={18} />
      <span>Loading capsule #{capsuleId}…</span>
    </div>
  );
}
